import React, { useCallback, useMemo, useState } from 'react';

import { transformAdvancementRequirementsForUpdate } from '@bsa/scouting-js-utils/src/advancements';
import { getRequirementUpdates } from '@bsa/scouting-js-utils/src/requirements';
import { Alert, Card, Form } from 'antd-v5';
import { useSelector } from 'react-redux';

import type { RequirementUpdateInfo } from '@appTypes/esb';
import { AdvancementAction } from '@modules/roster-lite/components/AdvancementDetails/types';
import RequirementsRenderer from '@modules/roster-lite/components/RequirementsRenderer/RequirementsRenderer';
import type {
  RequirementFormValues,
  RequirementNode,
} from '@modules/roster-lite/components/RequirementsRenderer/types';
import {
  buildRequirementsInitialValues,
  buildRequirementsTree,
  getRequirementSaveErrorMessage,
  isRequirementsLoading,
  mergeRequirements,
  toVersionOptions,
} from '@modules/roster-lite/components/RequirementsRenderer/utils';
import {
  useGetRankRequirementsQuery,
  useGetRanksQuery,
  useGetYouthRankRequirementsQuery,
  usePostYouthRankRequirementsMutation,
  useUpdateYouthRankVersionMutation,
} from '@modules/rtk/esb-api';
import { toastService } from '@modules/toasts';
import {
  isYouthMemberSel,
  organizationGuidSel,
  userIdSel,
} from '@shared/duck/selectors/general.selectors';
import { intl } from '@shared/localization';

interface RequirementsCardProps {
  /** Scoutbook user id of the youth whose record this is */
  userId: string;
  /** Matches the route param, which is not narrowed to a string */
  rankId: number | string;
  /** Controls the page's action policy greys */
  disabledActions?: AdvancementAction[];
}

/**
 * Fetches a rank's requirements, owns the `<Form>` the checklist renders into,
 * and saves one requirement at a time.
 *
 * Two calls are joined here: the catalog holds the requirement *text* per
 * version, the youth route holds their *progress*. Requirement ids are
 * version-scoped, so the catalog must be fetched for the version the youth is
 * actually on - which only the youth route knows
 */
export default function RequirementsCard(props: RequirementsCardProps) {
  const { userId, rankId, disabledActions } = props;

  const organizationGuid = useSelector(organizationGuidSel);
  const currentUserId = useSelector(userIdSel);
  const isYouthEditor = useSelector(isYouthMemberSel);

  const [savingRequirementId, setSavingRequirementId] = useState<number | null>(
    null,
  );

  const numericUserId = Number(userId);
  const numericRankId = Number(rankId);
  const skip = !numericUserId || !numericRankId;

  const youthQuery = useGetYouthRankRequirementsQuery(
    { rankId: numericRankId, userId: numericUserId },
    { skip },
  );

  /**
   * `currentData`, not `data`. RTK Query keeps `data` on the previous arg's result
   * while a new one is in flight, so on a rank jump this would still be the old
   * rank's version and the catalog query below would fire against it
   */
  const versionId = youthQuery.currentData?.versionId;

  const catalogQuery = useGetRankRequirementsQuery(
    { rankId: numericRankId, params: { versionId } },
    { skip: skip || !versionId },
  );

  const ranksQuery = useGetRanksQuery(undefined);

  const [postRequirements] = usePostYouthRankRequirementsMutation();
  const [updateVersion, versionState] = useUpdateYouthRankVersionMutation();

  // `currentData` on both sides too, otherwise every row would read as not started
  const requirements = useMemo(
    () =>
      buildRequirementsTree(
        mergeRequirements(
          catalogQuery.currentData?.requirements,
          youthQuery.currentData?.requirements,
        ),
      ),
    [catalogQuery.currentData, youthQuery.currentData],
  );

  const initialValues = useMemo(
    () => buildRequirementsInitialValues(requirements),
    [requirements],
  );

  const versionOptions = useMemo(() => {
    const rank = ranksQuery.data?.find(item => item.id === numericRankId);
    return toVersionOptions(rank?.versions ?? []);
  }, [ranksQuery.data, numericRankId]);

  const handleSave = useCallback(
    async (
      id: number,
      values: RequirementFormValues,
      root: RequirementNode,
    ) => {
      const completionDate = values.completionDate;
      const markAsApproved = values.approved;
      const canApprove = !disabledActions?.includes(AdvancementAction.Approve);

      // the root node carries the subtree, which is what drives the parent
      // cascade inside `getRequirementUpdates`
      const updates = getRequirementUpdates(
        root,
        [String(id)],
        completionDate ? completionDate.toJSDate() : null,
        Number(currentUserId),
        canApprove,
        markAsApproved,
        true,
        false,
      );

      const requirementsPayload = transformAdvancementRequirementsForUpdate(
        updates,
        'ranks',
      ) as RequirementUpdateInfo[];

      setSavingRequirementId(id);
      try {
        await postRequirements({
          rankId: numericRankId,
          payload: [
            {
              userId: numericUserId,
              organizationGuid,
              requirements: requirementsPayload,
            },
          ],
        }).unwrap();

        toastService.success(
          intl.formatMessage({ id: 'requirementsRenderer.saved' }),
        );
      } catch (error) {
        // the server usually explains the rejection better than we can
        toastService.error(
          getRequirementSaveErrorMessage(error) ??
            intl.formatMessage({ id: 'requirementsRenderer.saveError' }),
        );
        // rethrown so the row knows the save failed and keeps the edit
        throw error;
      } finally {
        setSavingRequirementId(null);
      }
    },
    [
      currentUserId,
      disabledActions,
      numericRankId,
      numericUserId,
      organizationGuid,
      postRequirements,
    ],
  );

  const handleVersionChange = useCallback(
    async (nextVersionId: number) => {
      try {
        await updateVersion({
          rankId: numericRankId,
          userId: numericUserId,
          payload: { versionId: nextVersionId },
        }).unwrap();
      } catch (error) {
        toastService.error(
          getRequirementSaveErrorMessage(error) ??
            intl.formatMessage({ id: 'requirementsRenderer.versionError' }),
        );
      }
    },
    [numericRankId, numericUserId, updateVersion],
  );

  const isError = youthQuery.isError || catalogQuery.isError;
  // lock while switching versionr getting youth ranks
  const revalidating = versionState.isLoading || youthQuery.isFetching;
  const loading = isRequirementsLoading({
    youthFetching: youthQuery.isFetching,
    catalogFetching: catalogQuery.isFetching,
    hasCatalog: Boolean(catalogQuery.currentData),
    hasRequirements: requirements.length > 0,
    isError,
  });

  if (isError && !loading) {
    return (
      <Card>
        <Alert
          type="error"
          showIcon
          message={intl.formatMessage({ id: 'requirementsRenderer.error' })}
        />
      </Card>
    );
  }

  return (
    <Card>
      {/* DO NOT REMOVE the `key`: antd applies `initialValues` on mount only, so
        without it fields stay empty on first load and stale after a version switch.
        The catalog half is load-bearing - the version arrives first, and remounting
        on it alone leaves the form seeded from a merge that had no requirements yet */}
      <Form
        key={`${rankId}-${versionId ?? 'loading'}-${
          catalogQuery.currentData ? 'ready' : 'loading'
        }`}
        layout="vertical"
        initialValues={initialValues}
      >
        <RequirementsRenderer
          requirements={requirements}
          advancementType="ranks"
          loading={loading}
          savingRequirementId={savingRequirementId}
          busy={revalidating}
          disabledActions={disabledActions}
          isYouthEditor={isYouthEditor}
          notes={catalogQuery.currentData?.rankInformation}
          onSaveRequirement={handleSave}
          version={{
            options: versionOptions,
            selectedVersionId: versionId,
            changing: revalidating,
            disabled:
              Boolean(youthQuery.data?.markedCompletedDate) ||
              disabledActions?.includes(AdvancementAction.CompletionDate),
            onChange: handleVersionChange,
          }}
        />
      </Form>
    </Card>
  );
}
